import { Image } from '@rocco/components/image';
import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import { useFeature } from '@modules/root';
import { SubscribePublicationsTrigger } from '@modules-rocco/subscription/component/SubscribePublicationsTrigger';

export interface PremiumArticleFeature {
    title: string;
    link: string;
    intro?: string;
    imageSrc?: string;
    /** Overrides the module's "Premium article" label; set by a curated placement. */
    eyebrow?: string;
    /** Overrides "Read More"; set by a curated placement. */
    ctaLabel?: string;
}

interface PremiumArticleModuleProps {
    article: PremiumArticleFeature;
    /** Small grey line above the headline. */
    eyebrow?: string;
    ctaLabel?: string;
    /**
     * `article` is the directory's premium feature (0:1696); `subscribe` is the
     * category page's inbox pitch (51:911). They share a frame and an image
     * slot but genuinely differ below that: subscribe sets its headline in caps
     * and its body in Gestura 33 across the full 805 column, sitting higher up,
     * where the feature uses TWK Ghost 22 in a narrower column further down.
     */
    variant?: 'article' | 'subscribe';
    /**
     * True when the image is a bundled asset rather than a CMS URL. The Image
     * loader rewrites srcs through the CDN proxy, which mangles a build-output
     * path, so bundled assets are passed straight through.
     */
    imageBundled?: boolean;
    className?: string;
}

/**
 * The interruptive editorial feature that breaks up the article grid.
 *
 * Figma 0:1696 (the `Featured Article` master, 1920x799 on white): copy set
 * against a 50px left edge with a 672x794 image held to the right margin.
 * Positioned as percentages of the frame so the composition holds its
 * proportions; type is rem, which is 16px at 1920.
 */
export const PremiumArticleModule = ({
    article,
    eyebrow = 'Premium article',
    ctaLabel = 'Read More',
    variant = 'article',
    imageBundled = false,
    className,
}: PremiumArticleModuleProps) => {
    const isSubscribe = variant === 'subscribe';
    // The subscribe tile's control opens the publications chooser rather than
    // leading anywhere: on this variant the module is the subscribe pitch, not
    // an article. The feature variant keeps its link to the article.
    const canChoosePublications = useFeature(
        'digital_publication_subscribe_2026',
    );
    const opensChooser = isSubscribe && canChoosePublications;

    return (
        <section
            aria-label={eyebrow}
            className={cn(
                'relative w-full bg-white',
                'aspect-[1920/799] max-md:aspect-auto max-md:py-[2.5rem]',
                className,
            )}
        >
            {/* Desktop: the Figma composition against the frame. */}
            <div className="absolute inset-0 max-md:hidden">
                {article.imageSrc && (
                    <div
                        className="absolute overflow-hidden"
                        style={{
                            left: '60.833%',
                            top: 0,
                            width: '35%',
                            height: '99.374%',
                        }}
                    >
                        <Image
                            src={article.imageSrc}
                            {...(imageBundled && {
                                overrideSrc: article.imageSrc,
                            })}
                            alt=""
                            fill={true}
                            fit="cover"
                        />
                    </div>
                )}

                <p
                    className="absolute m-0 font-sans text-[0.9375rem] leading-[1.22] font-medium tracking-[-0.0469rem] text-[#9a9a9a]"
                    style={{ left: '2.6042%', top: '0.125%' }}
                >
                    {eyebrow}
                </p>

                <h2
                    className={cn(
                        'absolute m-0 line-clamp-4 font-gestura text-[4.125rem] leading-none font-normal text-black',
                        isSubscribe && 'uppercase',
                    )}
                    style={{
                        left: '2.6042%',
                        top: '8.761%',
                        width: '41.927%',
                    }}
                >
                    {article.title}
                </h2>

                {/*
                 * The feature's intro is anchored by its own bottom edge, so
                 * the 39 the frame leaves between its last line and the control
                 * holds whether an article's intro runs to one line or three.
                 * Top-anchoring it opens that gap to the height of the lines
                 * the article did not have. The subscribe variant keeps its
                 * top: its body is fixed copy written to fill four lines.
                 */}
                {article.intro && (
                    <p
                        className={cn(
                            'absolute m-0 font-normal text-black',
                            isSubscribe
                                ? 'line-clamp-4 font-gestura text-[2.0625rem] leading-[1.25] tracking-[-0.0516rem]'
                                : 'line-clamp-3 font-ghost font-twk-ghost text-[1.375rem] leading-[1.6] tracking-[-0.0413rem]',
                        )}
                        style={{
                            left: '2.6042%',
                            ...(isSubscribe
                                ? { top: '48.375%' }
                                : { bottom: '16.946%' }),
                            width: isSubscribe ? '41.927%' : '32.364%',
                        }}
                    >
                        {article.intro}
                    </p>
                )}

                <div
                    className="absolute"
                    style={{
                        left: '2.6042%',
                        top: isSubscribe ? '75.284%' : '87.989%',
                    }}
                >
                    {opensChooser ? (
                        <SubscribePublicationsTrigger
                            label={ctaLabel}
                            className="h-[3.25rem] px-[1.723rem] text-[1rem]"
                        />
                    ) : (
                        <RoccoLink
                            to={article.link}
                            className="flex h-[3.25rem] items-center justify-center rounded-full bg-[#181818] px-[1.723rem] font-sans text-[1rem] leading-[1.3] font-medium text-[#f3f3f3] drop-shadow-[0_0.2027rem_0.7094rem_rgba(0,0,0,0.11)] transition-colors hover:bg-[#3d3d3d]"
                        >
                            {ctaLabel}
                        </RoccoLink>
                    )}
                </div>
            </div>

            {/* Mobile: image over stacked copy. */}
            <div className="flex flex-col gap-[1rem] px-[1.5rem] md:hidden">
                {article.imageSrc && (
                    <div className="relative aspect-[4/5] w-full overflow-hidden">
                        <Image
                            src={article.imageSrc}
                            {...(imageBundled && {
                                overrideSrc: article.imageSrc,
                            })}
                            alt=""
                            fill={true}
                            fit="cover"
                        />
                    </div>
                )}
                <p className="m-0 font-sans text-[0.9375rem] leading-[1.22] font-medium tracking-[-0.0469rem] text-[#9a9a9a]">
                    {eyebrow}
                </p>
                <h2
                    className={cn(
                        'm-0 font-gestura text-[2.25rem] leading-[1.05] font-normal text-black',
                        isSubscribe && 'uppercase',
                    )}
                >
                    {article.title}
                </h2>
                {article.intro && (
                    <p className="m-0 line-clamp-4 font-ghost font-twk-ghost text-[1rem] leading-[1.6] text-black">
                        {article.intro}
                    </p>
                )}
                {opensChooser ? (
                    <SubscribePublicationsTrigger
                        label={ctaLabel}
                        className="mt-[0.5rem] h-[2.75rem] px-[1.5rem] text-[0.875rem]"
                    />
                ) : (
                    <RoccoLink
                        to={article.link}
                        className="mt-[0.5rem] flex h-[2.75rem] w-fit items-center justify-center rounded-full bg-[#181818] px-[1.5rem] font-sans text-[0.875rem] font-medium text-[#f3f3f3]"
                    >
                        {ctaLabel}
                    </RoccoLink>
                )}
            </div>
        </section>
    );
};
